import { RoleGuard } from '@/features/authentication/components/RoleGuard';
import AuthCallbackPage from '@/features/authentication/pages/AuthCallbackPage';
import ProviderSelectionPage from '@/features/authentication/pages/ProviderSelectionPage';
import { AuthenticatedLayout } from '@/layouts/AuthenticatedLayout';
import { UserRole } from '@/types/roles';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Suspense, lazy } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import StyleGuidePage from '../features/style-guide/pages/StyleGuidePage';

// Lazy load heavy pages to reduce initial bundle size
const ReportingPage = lazy(
  () => import('@/features/reporting/pages/ReportingPage')
);
const CollectionReportPage = lazy(
  () => import('@/features/reporting/pages/CollectionReportPage')
);
const SurveyBuilderPage = lazy(
  () => import('@/features/survey-builder/pages/SurveyBuilderPage')
);
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'));
const ProjectPage = lazy(() => import('@/features/project/pages/ProjectPage'));
const PromptsPage = lazy(() => import('@/pages/PromptsPage'));
const SurveysPage = lazy(() => import('@/pages/SurveysPage'));

// Loading fallback component
const LoadingFallback = () => (
  <div className='flex justify-center items-center min-h-[400px]'>
    <ProgressSpinner />
  </div>
);

/**
 * App subdomain routes configuration
 * Used for the authenticated admin interface (app.hostname.com)
 */
export const appRoutes: RouteObject[] = [
  // Redirect root to surveys
  {
    path: '/',
    element: <Navigate to='/surveys' replace />,
  },

  // Authentication routes - standalone without layout
  {
    path: '/login',
    element: <ProviderSelectionPage />,
  },
  {
    path: '/auth/callback',
    element: <AuthCallbackPage />,
  },
  {
    path: '/auth/oidc-callback',
    element: <AuthCallbackPage />,
  },

  // Authenticated routes
  {
    element: <AuthenticatedLayout />,
    children: [
      {
        path: '/projects',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoleGuard
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORGANIZATION_ADMIN,
                UserRole.ORGANIZATION_EDITOR,
                UserRole.ORGANIZATION_READER,
              ]}
            >
              <ProjectsPage />
            </RoleGuard>
          </Suspense>
        ),
      },
      {
        path: '/projects/new',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoleGuard
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORGANIZATION_ADMIN,
                UserRole.ORGANIZATION_EDITOR,
              ]}
            >
              <ProjectPage />
            </RoleGuard>
          </Suspense>
        ),
      },
      {
        path: '/projects/edit/:projectId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoleGuard
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORGANIZATION_ADMIN,
                UserRole.ORGANIZATION_EDITOR,
              ]}
            >
              <ProjectPage />
            </RoleGuard>
          </Suspense>
        ),
      },
      {
        path: '/projects/:projectId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoleGuard
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORGANIZATION_ADMIN,
                UserRole.ORGANIZATION_EDITOR,
                UserRole.ORGANIZATION_READER,
              ]}
            >
              <ProjectPage />
            </RoleGuard>
          </Suspense>
        ),
      },
      {
        path: '/surveys',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoleGuard
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORGANIZATION_ADMIN,
                UserRole.ORGANIZATION_EDITOR,
                UserRole.ORGANIZATION_READER,
              ]}
            >
              <SurveysPage />
            </RoleGuard>
          </Suspense>
        ),
      },
      {
        path: '/surveys/new',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoleGuard
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORGANIZATION_ADMIN,
                UserRole.ORGANIZATION_EDITOR,
              ]}
            >
              <SurveyBuilderPage />
            </RoleGuard>
          </Suspense>
        ),
      },
      {
        path: '/surveys/edit/:surveyId',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoleGuard
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORGANIZATION_ADMIN,
                UserRole.ORGANIZATION_EDITOR,
              ]}
            >
              <SurveyBuilderPage />
            </RoleGuard>
          </Suspense>
        ),
      },
      {
        path: '/surveys/:id/report',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoleGuard
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORGANIZATION_ADMIN,
                UserRole.ORGANIZATION_EDITOR,
                UserRole.ORGANIZATION_READER,
              ]}
            >
              <ReportingPage />
            </RoleGuard>
          </Suspense>
        ),
      },
      {
        path: '/collections/:collectionId/report',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoleGuard
              allowedRoles={[
                UserRole.SUPER_ADMIN,
                UserRole.ORGANIZATION_ADMIN,
                UserRole.ORGANIZATION_EDITOR,
                UserRole.ORGANIZATION_READER,
              ]}
            >
              <CollectionReportPage />
            </RoleGuard>
          </Suspense>
        ),
      },
      {
        path: '/prompts',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <RoleGuard allowedRoles={[UserRole.SUPER_ADMIN]}>
              <PromptsPage />
            </RoleGuard>
          </Suspense>
        ),
      },
      {
        path: '/style-guide',
        element: (
          <RoleGuard allowedRoles={[UserRole.SUPER_ADMIN]}>
            <StyleGuidePage />
          </RoleGuard>
        ),
      },
    ],
  },

  // Catch-all: redirect unmatched routes to surveys
  {
    path: '*',
    element: <Navigate to='/surveys' replace />,
  },
];
